import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { apiService } from '../services/apiService';
import type { FeedHistoryItem } from '../services/apiService';

export interface FeedListRef {
  refresh: () => void;
}

const FeedList = forwardRef<FeedListRef>((_, ref) => {
  const [feeds, setFeeds] = useState<FeedHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchFeeds = async () => {
    setLoading(true);
    try {
      const data = await apiService.getFeedHistory();
      setFeeds(data.history);
    } catch (error) {
      console.error('Failed to fetch feeds:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeeds();
  }, []);

  useImperativeHandle(ref, () => ({
    refresh: fetchFeeds,
  }));

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  // Both scheduled AND manual URL syncs name the run "sync-feed-<id>.csv"
  // (scheduler.ts); direct uploads keep the uploaded filename. This is a
  // best-effort hint next to the always-visible filename, NOT an authority —
  // a direct upload literally named "sync-feed-*.csv" would also read "Sync".
  const runSource = (filename: string): 'Sync' | 'Upload' =>
    filename.startsWith('sync-feed-') ? 'Sync' : 'Upload';

  const HISTORY_HELP =
    'A log of each successful processing run — every successful upload or URL sync adds a row. ' +
    'Re-processing a feed replaces its products; it does not create a new feed. Your active ' +
    'feeds are listed on the Feeds page under “Your shops.”';

  if (loading) {
    return (
      <div className="feeds-list">
        <h3>Upload &amp; sync history</h3>
        <p>Loading history…</p>
      </div>
    );
  }

  if (feeds.length === 0) {
    return (
      <div className="feeds-list">
        <h3>Upload &amp; sync history</h3>
        <p style={{ color: 'var(--muted)', fontStyle: 'italic' }}>
          No processing runs yet. Add a shop or upload a feed to get started — each upload and
          auto-sync will show up here.
        </p>
      </div>
    );
  }

  return (
    <div className="feeds-list" data-testid="feeds-list">
      <h3>Upload &amp; sync history</h3>
      <p style={{ color: 'var(--muted)', fontSize: '0.9em', marginTop: '-0.25rem' }}>{HISTORY_HELP}</p>
      <div className="feeds-table">
        <div className="table-header" style={{ fontWeight: 'bold' }}>
          <span>Run</span>
          <span>Date</span>
          <span>Network</span>
          <span>Products</span>
          <span>Success Rate</span>
        </div>
        {feeds.map((feed, index) => (
          <div key={index} className="table-row" data-testid="feed-item">
            <span className="filename">
              <span
                style={{
                  fontSize: '0.75em', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em',
                  padding: '0.05rem 0.4rem', borderRadius: 'var(--radius-pill)', marginRight: '0.4rem',
                  backgroundColor: runSource(feed.filename) === 'Sync' ? 'var(--brand-soft)' : 'var(--good-bg)',
                  color: runSource(feed.filename) === 'Sync' ? 'var(--brand-dark)' : 'var(--good)',
                }}
              >
                {runSource(feed.filename)}
              </span>
              {feed.filename}
            </span>
            <span className="date">{formatDate(feed.created_at)}</span>
            <span className="network" style={{ textTransform: 'capitalize' }}>{feed.network}</span>
            <span className="count">
              {feed.processed_products}/{feed.total_products}
              {feed.skipped_products > 0 && (
                <span style={{ color: 'var(--warn)', fontSize: '0.85em' }}> ({feed.skipped_products} skipped)</span>
              )}
            </span>
            <span className={`status ${feed.success_rate === 100 ? 'completed' : 'processing'}`}>
              {feed.success_rate}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
});

FeedList.displayName = 'FeedList';

export default FeedList;
